import { useState } from "react";
import { useParams } from "react-router-dom";
import { Download, FileText } from "lucide-react";
import { DraftContextHeader } from "@/components/draft/DraftContextHeader";
import { DraftStepFooter } from "@/components/drafting/DraftStepFooter";
import { ErrorAlert } from "@/components/common/ErrorAlert";
import { GlassCard } from "@/components/common/GlassCard";
import { Button } from "@/components/ui/button";
import { downloadDraftDocument } from "@/lib/api/drafts";
import { useDraftMatter } from "@/hooks/useDraftMatters";

const EXPORT_OPTIONS = [
  {
    id: "docx" as const,
    title: "Clean Word draft",
    description: "Execution-ready .docx without tracked changes",
  },
  {
    id: "redline" as const,
    title: "Redline Word draft",
    description: "Tracked changes against the template baseline",
  },
  {
    id: "pdf" as const,
    title: "Execution PDF",
    description: "PDF copy with draft watermark",
  },
  {
    id: "summary" as const,
    title: "Executive summary PDF",
    description: "Thin key-terms summary for stakeholders",
  },
];

export function DraftExportPage() {
  const { id } = useParams<{ id: string }>();
  const { matter, isLoading, error } = useDraftMatter(id, { syncStepStatus: "export" });
  const [downloading, setDownloading] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExport(format: "docx" | "redline" | "pdf" | "summary") {
    if (!id) return;
    setDownloading(format);
    setExportError(null);
    try {
      await downloadDraftDocument(id, format);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setDownloading(null);
    }
  }

  if (isLoading) {
    return <p className="p-8 text-sm text-[var(--lm-muted)]">Loading export...</p>;
  }

  if (error || !matter) {
    return (
      <div className="p-8">
        <ErrorAlert message={error ?? "Draft matter not found"} />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="min-w-0 shrink-0">
        <DraftContextHeader matter={matter} currentStep={5} />
      </div>
      <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 md:p-6">
        <div className="mx-auto max-w-5xl space-y-4">
          {exportError ? <ErrorAlert message={exportError} /> : null}
          <GlassCard className="border-[var(--lm-border-card)] bg-white dark:border-glass-border dark:bg-slate-950/45">
            <h2 className="mb-1 text-base font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
              Export documents
            </h2>
            <p className="mb-6 text-sm text-[var(--lm-muted)] dark:text-slate-400">
              Download the current approved working version in your preferred format.
            </p>
            <div className="space-y-3">
              {EXPORT_OPTIONS.map((option) => (
                <div
                  key={option.id}
                  className="flex items-center justify-between gap-4 rounded-xl border border-[var(--lm-border-subtle)] p-4 dark:border-slate-700"
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="rounded-lg bg-violet-500/10 p-2">
                      <FileText className="h-4 w-4 text-violet-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
                        {option.title}
                      </p>
                      <p className="text-xs text-[var(--lm-muted)] dark:text-slate-400">
                        {option.description}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={downloading !== null}
                    onClick={() => handleExport(option.id)}
                    className="gap-2 shrink-0"
                  >
                    <Download className="h-4 w-4" />
                    {downloading === option.id ? "Exporting..." : "Download"}
                  </Button>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      </main>
      <DraftStepFooter matterId={matter.id} currentStep={5} />
    </div>
  );
}
